import { faMinusCircle, faPlusCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldArray, useFormikContext } from 'formik';
import type { FC } from 'react';
import { v4 as uuid } from 'uuid';
import { yesNoOptions } from 'src/apis/storage/mappings';
import type { MarketArea } from 'src/apis/storage/types';
import { Button } from 'src/components/buttons-and-actions/button';
import { Card } from 'src/components/data-display/card';
import { Editor } from 'src/components/form/editor';
import { formGapClasses } from 'src/components/form/form-container';
import type { FormOption } from 'src/components/form/form-option';
import { Input } from 'src/components/form/input';
import { Radios } from 'src/components/form/radios';
import { NetworkPointMultiSelect } from 'src/components/form/select/network-point-select/network-point-multi-select';
import { SearchableSingleSelect } from 'src/components/form/select/searchable-single-select';
import { Spacer } from 'src/components/layout/spacer';
import { Heading } from 'src/components/text/heading';
import { useClientSearch } from 'src/hooks/use-client-search';
import { useOnChange } from 'src/hooks/use-on-change';
import type {
  CreateStorageLocationFormValues,
  MarketAreaEntry,
} from 'src/pages/storage/locations/create/schema';
import { MAX_MARKET_AREAS } from 'src/pages/storage/locations/utils';
import { cn } from 'src/utils/cn';

type Props = {
  marketAreas: MarketArea[];
};

export const CreateStorageLocationFormFields: FC<Props> = ({ marketAreas }) => {
  const { values } = useFormikContext<CreateStorageLocationFormValues>();
  const marketAreaOptions = marketAreas.map((ma) => ({
    label: ma.name,
    value: ma.id,
  }));

  const canAddMarketArea = values.marketAreas.length < MAX_MARKET_AREAS;
  const isVirtual = values.virtualPoint === 'true';

  return (
    <>
      <Heading data-testid="identifier-section" mode="title" size="md">
        Identifier
      </Heading>
      <Input
        name="locationName"
        label="Location Name"
        placeholder="E.g. North Storage Zone 3"
        markAsRequired
      />
      <Radios
        name="virtualPoint"
        label="Virtual Point"
        options={yesNoOptions}
      />
      <Input
        name="locationEic"
        label="Location EIC"
        placeholder="E.g. 21XSTORAGE0001L"
        markAsRequired={!isVirtual}
      />
      <Spacer />

      <Heading data-testid="market-section" mode="title" size="sm">
        Market
      </Heading>

      <FieldArray
        name="marketAreas"
        render={(arrayHelpers) => (
          <div className={cn('grid', formGapClasses)}>
            {values.marketAreas.map((marketArea, index) => (
              <MarketAreaRow
                key={marketArea.id}
                index={index}
                marketAreaIdValue={marketArea.marketAreaId?.value ?? null}
                onRemove={() => arrayHelpers.remove(index)}
                options={marketAreaOptions}
              />
            ))}
            {canAddMarketArea && (
              <div className="flex justify-end">
                <Button
                  mode="link"
                  onClick={() => {
                    arrayHelpers.push({
                      id: uuid(),
                      marketAreaId: null,
                      injectionPoints: [],
                      withdrawalPoints: [],
                    } satisfies MarketAreaEntry);
                  }}
                  data-testid="add-market-area"
                >
                  <FontAwesomeIcon icon={faPlusCircle} className="mr-xs" />
                  Add Market Area
                </Button>
              </div>
            )}
          </div>
        )}
      />

      <Heading data-testid="general-information-section" mode="title" size="sm">
        General Information
      </Heading>
      <Editor name="comment" label="Comments" />
      <Spacer />
      <Input name="link" label="Link" placeholder="E.g. https://example.com" />
    </>
  );
};

const MarketAreaRow: FC<{
  index: number;
  marketAreaIdValue: string | null;
  onRemove: () => void;
  options: FormOption[];
}> = ({ index, marketAreaIdValue, onRemove, options }) => {
  const { setFieldValue, setFieldError } =
    useFormikContext<CreateStorageLocationFormValues>();

  const marketAreasSearch = useClientSearch(options, (opt, search) =>
    opt.label.toLowerCase().match(search)
  );

  const injectionPath = `marketAreas[${index}].injectionPoints`;
  const withdrawalPath = `marketAreas[${index}].withdrawalPoints`;

  useOnChange(() => {
    setFieldValue(injectionPath, []);
    setFieldError(injectionPath, undefined);
    setFieldValue(withdrawalPath, []);
    setFieldError(withdrawalPath, undefined);
  }, marketAreaIdValue);

  return (
    <Card mode="default" data-testid={`market-area-row-${index}`}>
      <div className="grid grid-flow-col justify-between">
        <Heading mode="title" size="md">
          Market Access {index + 1}
        </Heading>

        {index > 0 && (
          <Button
            mode="link"
            onClick={onRemove}
            data-testid={`remove-market-area-${index}`}
          >
            <FontAwesomeIcon icon={faMinusCircle} /> Remove
          </Button>
        )}
      </div>

      <div className={cn('mt-md grid gap-y-md', formGapClasses)}>
        <SearchableSingleSelect
          name={`marketAreas[${index}].marketAreaId`}
          label="Market Area"
          placeholder="E.g. Market Area East"
          markAsRequired
          {...marketAreasSearch}
        />
        {marketAreaIdValue && (
          <>
            <NetworkPointMultiSelect
              name={injectionPath}
              label="List of Injection Points"
              placeholder="Search for injection points"
              direction="EXIT"
            />
            <NetworkPointMultiSelect
              name={withdrawalPath}
              label="List of Withdrawal Points"
              placeholder="Search for withdrawal points"
              direction="ENTRY"
            />
          </>
        )}
      </div>
    </Card>
  );
};
